import { ReactNode } from 'react';
import styled from 'styled-components';
import { radius, colors, shadowS } from '@ardoq/design-tokens';
import { BULK_POPOVER_ATTR } from '@ardoq/popovers';
import { Stack } from '@ardoq/layout';
import { caption, text2 } from '@ardoq/typography';
import { formatOtherLabel } from './otherLabelUtils';
import {
  NEW_CONDITIONAL_FORMATTING_LEAF_MULTILABEL_BACKGROUNDCOLOR as BACKGROUND_COLOR,
  NEW_CONDITIONAL_FORMATTING_MULTILABEL_BORDERCOLOR as BORDER_COLOR,
  NEW_CONDITIONAL_FORMATTING_MULTILABEL_COLOR as TEXT_COLOR,
} from './multiLabelStyles';

export const OTHER_LABEL_MAX_LINES = 24;

type OtherLabelStylePreset = 'shadowed' | 'unshadowed' | 'legacy';
type OtherLabelElementProps = {
  $preset: OtherLabelStylePreset;
};
const OtherLabelElement = styled.label<OtherLabelElementProps>`
  display: inline-block;
  position: relative;
  padding: 2px 8px;
  margin: 4px 0 0 0;
  border-radius: ${radius.r4};
  border: 1px solid
    ${({ $preset }) => ($preset === 'legacy' ? colors.blue300 : BORDER_COLOR)};

  background: ${({ $preset }) =>
    $preset === 'legacy' ? colors.blue100 : BACKGROUND_COLOR};

  color: ${({ $preset }) =>
    $preset === 'legacy' ? colors.blue800 : TEXT_COLOR};

  box-shadow: ${({ $preset }) => ($preset === 'shadowed' ? shadowS : 'none')};
  cursor: pointer;
  min-width: fit-content;
  overflow-wrap: anywhere;
`;

const OtherLabelText = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${OTHER_LABEL_MAX_LINES};
  overflow: hidden;
`;

const PreWrappedLine = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  line-height: inherit;
`;

const PreWrappedLinesContainer = styled.span`
  display: block;
  overflow: hidden;
`;

const OtherLabelPopoverContainer = styled(Stack)`
  max-width: 448px;
`;

const OtherLabelTitle = styled.label`
  ${caption};
  color: ${colors.grey500};
`;
const OtherLabelCaption = styled.label`
  ${text2};
  color: ${colors.grey950};
`;

const OTHER_LABEL_TEXT_ATTR = 'data-otherlabel-text';

export const resolveOtherLabelPopover = (
  element: HTMLElement | SVGElement
): ReactNode | null => {
  const title = element.getAttribute('data-title');
  const caption = element.getAttribute('data-caption');
  if (title === null && caption === null) {
    return null;
  }
  const target = element.querySelector(`[${OTHER_LABEL_TEXT_ATTR}]`) ?? element;
  const isTruncated =
    target.scrollWidth > target.clientWidth ||
    target.scrollHeight > target.clientHeight;
  if (!element.textContent || !isTruncated) {
    return null;
  }
  return (
    <OtherLabelPopoverContainer>
      <OtherLabelTitle>{title}</OtherLabelTitle>
      <OtherLabelCaption>{caption}</OtherLabelCaption>
    </OtherLabelPopoverContainer>
  );
};

type OtherLabelProperties = {
  title: string;
  caption: string;
  showTitle: boolean;
  preset: OtherLabelStylePreset;
  wrappedLines?: string[];
};
export const OtherLabelBadge = ({
  title,
  caption,
  showTitle,
  preset,
  wrappedLines,
}: OtherLabelProperties) => (
  <OtherLabelElement
    $preset={preset}
    data-title={title}
    data-caption={caption}
    {...{ [BULK_POPOVER_ATTR]: 'true' }}
  >
    {wrappedLines ? (
      <PreWrappedLinesContainer {...{ [OTHER_LABEL_TEXT_ATTR]: true }}>
        {wrappedLines.map((line, index) => (
          <PreWrappedLine key={index}>{line}</PreWrappedLine>
        ))}
      </PreWrappedLinesContainer>
    ) : (
      <OtherLabelText {...{ [OTHER_LABEL_TEXT_ATTR]: true }}>
        {formatOtherLabel([title, caption, showTitle])}
      </OtherLabelText>
    )}
  </OtherLabelElement>
);
